<template>
  <div class="drp-wrapper" ref="wrapper">
    <div class="form-control drp-input" @click="toggleCalendar">
      <span v-if="startDate || endDate">{{ displayText }}</span>
      <span v-else class="text-secondary">{{ placeholder }}</span>
      <i class="fa fa-calendar drp-icon" aria-hidden="true"></i>
    </div>

    <div class="drp-popover" v-if="open">
      <div class="drp-header">
        <i class="fa fa-chevron-left drp-nav" aria-hidden="true" @click="shiftMonth(-1)"></i>
        <span class="drp-month-label">{{ monthLabel }} {{ viewYear }}</span>
        <i class="fa fa-chevron-right drp-nav" aria-hidden="true" @click="shiftMonth(1)"></i>
      </div>
      <div class="drp-weekdays">
        <span v-for="d in weekdayLabels" :key="d">{{ d }}</span>
      </div>
      <div class="drp-days">
        <span
          v-for="(cell, idx) in dayCells"
          :key="idx"
          class="drp-day"
          :class="{
            'drp-day-muted': !cell.inMonth,
            'drp-day-today': cell.isToday,
            'drp-day-start': cell.isStart,
            'drp-day-end': cell.isEnd,
            'drp-day-inrange': cell.inRange,
          }"
          @click="pickDate(cell.date)"
        >{{ cell.day }}</span>
      </div>
      <div class="drp-footer">
        <button type="button" class="btn btn-sm btn-link text-secondary mb-0" @click="clear">Hapus</button>
        <button type="button" class="btn btn-sm btn-link text-primary mb-0" @click="open = false">Selesai</button>
      </div>
    </div>
  </div>
</template>

<script>
var moment = require('moment');

// Self-contained date-range picker -- no external dependency, so there's
// no transpile/peer-dep risk on build (see conversation: @vuepic/vue-
// datepicker's dist bundle needed babel transpileDependencies tweaks that
// didn't reliably reproduce across environments, and a fresh npm install
// on the production server was itself a new failure point). First click
// sets the start date, second click sets the end date (swapping if the
// second click lands before the first); clicking inside an existing range
// starts a new selection.
export default {
  name: 'date-range-picker',
  props: {
    startDate: { type: String, default: '' },
    endDate: { type: String, default: '' },
    placeholder: { type: String, default: 'Pilih rentang tanggal' },
  },
  emits: ['update:startDate', 'update:endDate'],
  data() {
    var base = this.startDate ? moment(this.startDate) : moment();
    return {
      open: false,
      viewYear: base.year(),
      viewMonth: base.month(), // 0-11
      weekdayLabels: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
    };
  },
  computed: {
    displayText() {
      var start = this.startDate ? moment(this.startDate).format('DD/MM/YYYY') : '...';
      var end   = this.endDate ? moment(this.endDate).format('DD/MM/YYYY') : '...';
      return start + ' - ' + end;
    },
    monthLabel() {
      return moment().month(this.viewMonth).locale('id').format('MMMM');
    },
    dayCells() {
      var firstOfMonth = moment([this.viewYear, this.viewMonth, 1]);
      var startOffset  = firstOfMonth.day(); // 0 = Sunday
      var gridStart    = firstOfMonth.clone().subtract(startOffset, 'days');
      var cells        = [];
      var today        = moment().format('YYYY-MM-DD');

      for (var i = 0; i < 42; i++) {
        var d       = gridStart.clone().add(i, 'days');
        var dateStr = d.format('YYYY-MM-DD');
        cells.push({
          date: dateStr,
          day: d.date(),
          inMonth: d.month() === this.viewMonth,
          isToday: dateStr === today,
          isStart: dateStr === this.startDate,
          isEnd: dateStr === this.endDate,
          inRange: this.startDate && this.endDate && dateStr > this.startDate && dateStr < this.endDate,
        });
      }
      return cells;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggleCalendar() {
      this.open = !this.open;
    },
    handleOutsideClick(e) {
      if (this.open && this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.open = false;
      }
    },
    shiftMonth(delta) {
      var m = moment([this.viewYear, this.viewMonth, 1]).add(delta, 'months');
      this.viewYear  = m.year();
      this.viewMonth = m.month();
    },
    pickDate(dateStr) {
      // Fresh pick (no start yet, or both already set) -- start a new range.
      if (!this.startDate || (this.startDate && this.endDate)) {
        this.$emit('update:startDate', dateStr);
        this.$emit('update:endDate', '');
        return;
      }
      // Second click -- becomes the end, swapping if it's before the start.
      if (dateStr < this.startDate) {
        this.$emit('update:endDate', this.startDate);
        this.$emit('update:startDate', dateStr);
      } else {
        this.$emit('update:endDate', dateStr);
      }
      this.open = false;
    },
    clear() {
      this.$emit('update:startDate', '');
      this.$emit('update:endDate', '');
    },
  },
};
</script>

<style scoped>
.drp-wrapper {
  position: relative;
}
.drp-input {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
}
.drp-icon {
  color: #8392AB;
  font-size: 0.85rem;
}
.drp-popover {
  position: absolute;
  z-index: 1200;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 12px;
  width: 280px;
}
.drp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.875rem;
}
.drp-nav {
  cursor: pointer;
  padding: 4px 8px;
  color: #525f7f;
}
.drp-nav:hover {
  color: #11cdef;
}
.drp-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.7rem;
  color: #8392AB;
  margin-bottom: 4px;
}
.drp-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
}
.drp-day {
  text-align: center;
  font-size: 0.8rem;
  padding: 6px 0;
  cursor: pointer;
  border-radius: 0.375rem;
}
.drp-day:hover {
  background-color: #f0f8ff;
}
.drp-day-muted {
  color: #c0c4cc;
}
.drp-day-today {
  font-weight: 700;
  color: #11cdef;
}
.drp-day-inrange {
  background-color: #e3f6fd;
  border-radius: 0;
}
.drp-day-start,
.drp-day-end {
  background-color: #11cdef;
  color: #fff;
  font-weight: 600;
}
.drp-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  border-top: 1px solid #f0f2f5;
  padding-top: 6px;
}
</style>
